import React, { useState } from "react";
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";

const QuestionForm = ({ candidate, onClose, onInterviewTaken }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [displayedQuestions, setDisplayedQuestions] = useState([]);
  const [isTypingDone, setIsTypingDone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [interviewData, setInterviewData] = useState(null); // 👈 interview fetch ke liye
  const token = decryptToken("accessToken") || ""

  const apiUrl = process.env.REACT_APP_LINK;

  if (!candidate) {
    return (
      <p className="text-center py-6 text-sm text-red-600">
        No candidate selected
      </p>
    );
  }

  // --- Generate Questions ---
  const generateQuestions = async () => {
    setIsTypingDone(false);
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/question`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          CandidateName: candidate.CandidateName,
          Role: candidate.Role,
          Experience: candidate.Experience,
          CandidateEmail: candidate.CandidateEmail,
          Prompt: customPrompt,
        }),
      });

      const data = await response.json();
      const raw = data.questions || data.text || "";

      const qaRegex =
        /Question\s?\d+[:.]\s*([\s\S]*?)\s*Answer[:.]\s*([\s\S]*?)(?=Question\s?\d+[:.]|$)/gi;
      const parsed = [];
      let match;

      while ((match = qaRegex.exec(raw)) !== null) {
        parsed.push({
          question: match[1].trim().replace(/\*\*/g, ""),
          answer: match[2].trim().replace(/\*\*/g, ""),
        });
      }

      setQuestions(parsed);
      setDisplayedQuestions([]);

      let currentIndex = 0;
      const typedQuestions = [];

      const typeQuestionAnswer = async () => {
        if (currentIndex >= parsed.length) return;
        const { question, answer } = parsed[currentIndex];
        let qText = "";
        let aText = "";

        for (let i = 0; i <= question.length; i++) {
          qText = question.substring(0, i);
          setDisplayedQuestions([
            ...typedQuestions,
            { question: qText, answer: "" },
          ]);
          await new Promise((res) => setTimeout(res, 15));
        }

        for (let j = 0; j <= answer.length; j++) {
          aText = answer.substring(0, j);
          setDisplayedQuestions([
            ...typedQuestions,
            { question, answer: aText },
          ]);
          await new Promise((res) => setTimeout(res, 15));
        }

        typedQuestions.push({ question, answer });
        currentIndex++;

        if (currentIndex === parsed.length) {
          setIsTypingDone(true);
          return;
        }

        typeQuestionAnswer();
      };

      typeQuestionAnswer();
    } catch (err) {
      console.error("Question generation error:", err);
      toast.error("Could not load interview questions.");
    } finally {
      setLoading(false);
    }
  };

  // --- Export Handlers ---
  const copyToClipboard = () => {
    const text = questions
      .map((q) => `${q.question}\n${q.answer}`)
      .join("\n\n");
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied to clipboard!"));
  };

  const downloadAsPDF = () => {
    const doc = new jsPDF();
    let y = 20;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`Interview Questions: ${candidate.CandidateName}`, 10, 10);

    doc.setFontSize(12);
    questions.forEach((q, index) => {
      doc.setFont("helvetica", "bold");
      const questionLines = doc.splitTextToSize(
        `Q${index + 1}: ${q.question}`,
        180
      );
      doc.text(questionLines, 10, y);
      y += questionLines.length * 7;

      doc.setFont("helvetica", "normal");
      const answerLines = doc.splitTextToSize(`Ans: ${q.answer}`, 180);
      doc.text(answerLines, 10, y);
      y += answerLines.length * 7 + 10;

      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });

    doc.save(
      `${candidate.CandidateName.replace(/\s+/g, "_")}_Interview_Questions.pdf`
    );
  };

  const downloadAsDocx = async () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: `Interview Questions for ${candidate.CandidateName}`,
                  bold: true,
                  size: 28,
                }),
              ],
              spacing: { after: 300 },
            }),
            ...questions.flatMap((q, i) => [
              new Paragraph({
                text: `Q${i + 1}: ${q.question}`,
                bold: true,
                spacing: { after: 100 },
              }),
              new Paragraph({
                text: `Ans: ${q.answer}`,
                spacing: { after: 300 },
              }),
            ]),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(
      blob,
      `${candidate.CandidateName.replace(/\s+/g, "_")}_Interview_Questions.docx`
    );
  };


  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-[99999] flex flex-col justify-end items-center">
        <div className="bg-white w-full max-h-[90vh] max-w-[600px] rounded-t-2xl shadow-xl p-5 overflow-y-auto transition-transform duration-300 animate-slide-up">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Interview Questions for {candidate.CandidateName}</h2>
            <button
              onClick={() => setShowConfirm(true)}
              className="text-gray-500 hover:text-red-500 text-sm font-medium"
            >
              <X />
            </button>
          </div>

          <div className="mb-4 text-gray-600 space-y-1">
            <p>
              <span className="font-semibold text-gray-800">Role:</span>{" "}
              {candidate.Role}
            </p>
            <p>
              <span className="font-semibold text-gray-800">Experience:</span>{" "}
              {candidate.Experience} years
            </p>
            <p>
              <span className="font-semibold text-gray-800">Email:</span>{" "}
              {candidate.CandidateEmail}
            </p>
          </div>

          <div className="mb-4">
            <label className="font-medium text-gray-700 block mb-1">
              Custom Prompt (optional)
            </label>
            <textarea
              rows="3"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="e.g. Focus on system design and logical reasoning..."
              className="w-full p-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            />
          </div>

          <div className="mb-4 flex gap-2">
            <button
              onClick={generateQuestions}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm shadow-sm disabled:opacity-50"
            >
              {loading ? "Generating..." : "Generate Questions"}
            </button>
            <button
              onClick={() => setQuestions([])}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
            >
              Reset
            </button>
          </div>

          {questions.length > 0 && (
            <>
              <div className="space-y-4 mb-4">
                {displayedQuestions.map((q, i) => (
                  <div
                    key={i}
                    className="p-3 border border-gray-200 rounded-md bg-blue-50"
                  >
                    <p className="font-semibold text-blue-800 mb-1">
                      Q.{i + 1}: {q.question}
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      <span className="font-semibold">Ans:</span> {q.answer}
                    </p>
                  </div>
                ))}
              </div>

              {isTypingDone && (
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-md text-xs"
                  >
                    Copy All
                  </button>
                  <button
                    onClick={downloadAsPDF}
                    className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-md text-xs"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={downloadAsDocx}
                    className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-xs"
                  >
                    Download DOCX
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showConfirm && (
        <ConfirmPopup
          message="Are you sure you want to close this interview? You won't be able to open it again."
          onConfirm={async () => {
            setShowConfirm(false);

            if (candidate?.CandidateEmail) {
              try {
                const res = await fetch(`${apiUrl}/question/mark-taken`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json","Authorization": `Bearer ${token}` },
                  body: JSON.stringify({ email: candidate.CandidateEmail }),
                });

                const data = await res.json();
                if (data.success) {
                  toast.success("Interview marked as taken ✅");
                  if (onInterviewTaken) {
                    onInterviewTaken(candidate.CandidateEmail.toLowerCase());
                  }
                } else {
                  toast.error("Failed to update interview status.");
                }
              } catch (err) {
                console.error("❌ Error marking interview taken:", err);
                toast.error("Server error while updating interview status.");
              }
            }

            onClose();
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}


    </>
  );
};

export default QuestionForm;


const ConfirmPopup = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-[100000] backdrop-blur-sm">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-[420px] max-w-[95%] text-center">
        <p className="text-gray-900 text-lg font-medium mb-6">{message}</p>

        <div className="flex justify-center gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm shadow-sm disabled:opacity-50"
          >
            Yes, Interview Done
          </button>
        </div>
      </div>
    </div>
  );
};
